/**
 * @file 認證服務 API
 * @description 提供使用者認證相關的 API 方法，包含登入、登出、Token 刷新等功能
 */
import { post, get } from './sub_services/base';
import config from 'config/config';
import apiPath from './api.path';
import ExceptionHandleService from 'utils/exceptionHandler';
import localStorageUtil from 'utils/localStorageUtil';
import LocalStorageKeys from 'constants/localStorageKeys';

const _EHS = new ExceptionHandleService({
  _NAME: 'service/auth.ts',
  _NOTICE: 'auth api'
});

/** 登入請求參數介面 */
export interface LoginParams {
  act: string;
  pwd: string;
}

/** 認證資訊回應介面 */
export interface AuthInfo {
  accessToken?: string;
  refreshToken?: string;
  [key: string]: unknown;
}

/** [POST]後台登入 */
const login = async (params: LoginParams): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.auth.login}`;
    const result = await post(url, params);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'login', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [GET]透過token取得用戶資訊 */
const getAuthInfo = async (): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.auth.getAuthInfo}`;
    const result = await get(url);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'getAuthInfo', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [GET]刷新 token */
const refresh = async (): Promise<AuthInfo | null> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.auth.refresh}`;
    const userInfo = localStorageUtil.getItem(LocalStorageKeys.UserInfo) as {
      accessToken?: string;
      refreshToken?: string;
    } | null;

    const token = userInfo?.accessToken || '';
    const rToken = userInfo?.refreshToken || '';

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'refresh-token': `Bearer ${rToken}`,
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    const data = await response.json();
    if (response.ok && data?.statusCode === 1000) {
      return data.result as AuthInfo;
    }
    return null;
  } catch (error) {
    _EHS.errorReport(error, 'refresh', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [GET]後台登出 */
const logout = async (): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.auth.logout}`;
    const result = await get(url);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'logout', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/** [GET]dashboard 首頁資訊 */
const getDashboard = async (): Promise<unknown> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.auth.getDashboard}`;
    const result = await get(url);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'getDashboard', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

export default {
  login,
  getAuthInfo,
  refresh,
  logout,
  getDashboard
};
